<script lang="ts" setup>
import { computed, ref } from "vue";
import {
  ButtonSeverity,
  TuIconButton,
  TuPopover,
  TuTooltipWrapper,
  usePopover,
} from "@tinyurldev/tu-ui-vue";

import useTranslation from "@Composables/useTranslation";

interface Props {
  isArchived?: boolean;
  enabled?: boolean;
}

//
// Setup
const { __ } = useTranslation();

//
// Props
withDefaults(defineProps<Props>(), {
  isArchived: false,
  enabled: false,
});

//
// Emit
const emit = defineEmits<{
  (e: "archive"): void;
  (e: "delete"): void;
  (e: "toggle-stats"): void;
}>();

//
// Data
const isPopoverVisible = ref(false);
const { referenceRef, popoverRef } = usePopover(isPopoverVisible, { placement: "bottom-end" });

//
// Computed
const tooltip = computed(() => {
  if (isPopoverVisible.value) {
    return;
  }

  return __("organization_dashboard.alias_toolbar.alias_settings");
});

//
// Methods
function onToggle(): void {
  isPopoverVisible.value = !isPopoverVisible.value;
}

function onArchiveClick(): void {
  isPopoverVisible.value = false;
  emit("archive");
}

function onDeleteClick(): void {
  isPopoverVisible.value = false;
  emit("delete");
}

function onToggleStats(): void {
  isPopoverVisible.value = false;
  emit("toggle-stats");
}
</script>

<template>
  <div class="v2-settings-dropdown">
    <TuTooltipWrapper :tooltip="tooltip">
      <TuIconButton
        ref="referenceRef"
        class="_settings"
        :aria-label="__('organization_dashboard.alias_toolbar.more_options')"
        :title="__('organization_dashboard.alias_toolbar.more_options')"
        icon-class="fa-solid fa-ellipsis-h"
        is-outlined
        :severity="ButtonSeverity.CANCEL"
        @click="onToggle"
      />
    </TuTooltipWrapper>

    <TuPopover
      v-show="isPopoverVisible"
      ref="popoverRef"
    >
      <div class="v2-settings-dropdown__menu _settings_menu">
        <button
          class="v2-settings-dropdown__menu-item _archive"
          type="button"
          @click="onArchiveClick"
        >
          <i class="fa fa-fw fa-archive" />
          {{ isArchived ? __('organization_dashboard.alias_toolbar.unarchive') : __('organization_dashboard.alias_toolbar.archive') }}
        </button>
        <button
          class="v2-settings-dropdown__menu-item _toggle_stats"
          type="button"
          @click="onToggleStats"
        >
          <i class="fa-solid fa-chart-column" />
          {{ 
            enabled
              ? __('organization_dashboard.alias_toolbar.disable_toggle_stats')
              : __('organization_dashboard.alias_toolbar.enable_toggle_stats')
          }}
        </button>
        <button
          class="v2-settings-dropdown__menu-item v2-settings-dropdown__menu-item--delete _delete"
          type="button"
          @click="onDeleteClick"
        >
          <i class="fa fa-fw fa-trash" />
          {{ __('organization_dashboard.alias_toolbar.delete') }}
        </button>
      </div>
    </TuPopover>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.v2-settings-dropdown {
  &__menu {
    display: flex;
    flex-direction: column;
    min-width: 120px;
    padding: spacing.$xs-5 0;

    background-color: color.$background-secondary;
    border-radius: border.$medium-radius;
  }

  &__menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;

    font-size: 13px;
    text-align: left;
    color: color.$text;

    background: none;
    border: none;
    cursor: pointer;

    &:hover {
      background-color: color.$background-primary;
    }

    &--delete {
      color: color.$alert;
    }
  }
}
</style>
